import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useApi } from '../../apiHelpers';
import { Dialog } from '../../components/ui/Dialog';
import { EmptyState } from '../../components/ui/EmptyState';
import FadeIn from '../../components/ui/FadeIn';
import { CATEGORY } from '../../components/ui/categoryColors';
import { ListRow, ListStack } from '../../components/ui/ListRow';
import { Skeleton, SkeletonGroup } from '../../components/ui/Skeleton';
import { geocodeCityState, formatCityState } from '../../utils/geocode';
import 'leaflet/dist/leaflet.css';

dayjs.extend(relativeTime);

// Thin wrapper preserving this modal's existing string-return shape.
// The shared util handles caching, in-flight dedup, and rate limiting.
async function geocodeLocation(lat, lon) {
    return formatCityState(await geocodeCityState(lat, lon));
}

// Create numbered marker icon
function createNumberedIcon(number, isSelected = false) {
    const color = isSelected ? '#FFCC33' : CATEGORY.blue.mark.hex;
    const size = isSelected ? 28 : 22;
    const glow = isSelected
        ? 'box-shadow: 0 0 12px rgba(255,204,51,0.6), 0 2px 8px rgba(0,0,0,0.4);'
        : 'box-shadow: 0 2px 8px rgba(0,0,0,0.4);';

    return L.divIcon({
        className: 'custom-marker',
        html: `
            <div style="
                width: ${size}px;
                height: ${size}px;
                background: ${color};
                border: 2px solid ${isSelected ? '#fff' : 'rgba(255,255,255,0.8)'};
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: ${isSelected ? '12px' : '10px'};
                font-weight: bold;
                color: black;
                ${glow}
                transition: all 0.15s ease;
            ">${number}</div>
        `,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });
}

// Component to fit map bounds
function FitBounds({ positions }) {
    const map = useMap();

    useEffect(() => {
        if (positions.length > 0) {
            const bounds = L.latLngBounds(positions);
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
        }
    }, [positions, map]);

    return null;
}

function LocationHistoryModal({ isOpen, onClose, serialNumber, isDesktop }) {
    const api = useApi();
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [locationNames, setLocationNames] = useState({});
    const [selectedId, setSelectedId] = useState(null);

    // Fetch history data immediately
    useEffect(() => {
        if (!isOpen) return;

        let isMounted = true;

        const fetchHistory = async () => {
            setLoading(true);
            try {
                const data = await api.get(`/location-history/${serialNumber}?limit=50`);
                if (isMounted) {
                    setHistory(data);
                }
            } catch (error) {
                console.error('Failed to fetch location history:', error);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchHistory();
        return () => { isMounted = false; };
    }, [isOpen, serialNumber]);

    // Lazy geocode locations in background after data loads
    useEffect(() => {
        if (history.length === 0) return;

        let isMounted = true;

        const geocodeInBackground = async () => {
            const withCoords = history.filter(h => h.lat !== null && h.lon !== null);
            for (let i = 0; i < withCoords.length; i++) {
                if (!isMounted) break;
                const entry = withCoords[i];
                const cacheKey = `${entry.lat},${entry.lon}`;
                // Skip if already geocoded
                if (locationNames[cacheKey]) continue;
                // Rate limit after first request
                if (i > 0) await new Promise(r => setTimeout(r, 1100));
                const name = await geocodeLocation(entry.lat, entry.lon);
                if (isMounted && name) {
                    setLocationNames(prev => ({ ...prev, [cacheKey]: name }));
                }
            }
        };

        geocodeInBackground();
        return () => { isMounted = false; };
    }, [history]);

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = dayjs(dateString);
        const hoursDiff = dayjs().diff(date, 'hour');
        if (hoursDiff < 24) {
            return date.fromNow();
        }
        return date.format('MMM D, YYYY [at] h:mm A');
    };

    // Filter entries with valid coordinates for map
    const validEntries = useMemo(() =>
        history.filter(h => h.lat !== null && h.lon !== null),
        [history]
    );

    // Create polyline path (oldest to newest for the trail)
    const pathPositions = useMemo(() =>
        [...validEntries].reverse().map(e => [parseFloat(e.lat), parseFloat(e.lon)]),
        [validEntries]
    );

    // Default center (US center) or first entry
    const defaultCenter = validEntries.length > 0
        ? [parseFloat(validEntries[0].lat), parseFloat(validEntries[0].lon)]
        : [39.8283, -98.5795];

    return (
        <Dialog
            open={!!isOpen}
            onClose={onClose}
            size="2xl"
            title={(
                <>
                    Location History
                    <span className="text-primary ml-2 font-mono">{serialNumber}</span>
                </>
            )}
        >
            {/* Full-bleed: the map and list fill the dialog body edge to edge */}
            <div className={`-mx-5 -mb-5 -mt-1 flex overflow-hidden ${isDesktop ? 'h-[70dvh] flex-row' : 'h-[75dvh] flex-col'}`}>
                {/* Map */}
                <div className={`${isDesktop ? 'w-2/3' : 'h-1/2'} relative isolate bg-[#0d0d0d]`}>
                    <FadeIn key={loading ? 'loading' : validEntries.length === 0 ? 'empty' : 'map'} className="absolute inset-0">
                    {loading ? (
                        <SkeletonGroup label="Loading map" className="absolute inset-0">
                            <Skeleton className="h-full w-full rounded-none" />
                        </SkeletonGroup>
                    ) : validEntries.length === 0 ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <EmptyState variant="area">No GPS data available</EmptyState>
                        </div>
                    ) : (
                        <MapContainer
                            center={defaultCenter}
                            zoom={10}
                            style={{ height: '100%', width: '100%' }}
                            scrollWheelZoom={true}
                            attributionControl={false}
                            zoomControl={false}
                        >
                            <TileLayer
                                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                            />

                            <FitBounds positions={pathPositions} />

                            {/* Path line */}
                            {pathPositions.length > 1 && (
                                <Polyline
                                    positions={pathPositions}
                                    color={CATEGORY.blue.mark.hex}
                                    weight={2}
                                    opacity={0.5}
                                    dashArray="8, 8"
                                />
                            )}

                            {/* Markers */}
                            {validEntries.map((entry, index) => (
                                <Marker
                                    key={entry.id}
                                    position={[parseFloat(entry.lat), parseFloat(entry.lon)]}
                                    icon={createNumberedIcon(validEntries.length - index, selectedId === entry.id)}
                                    eventHandlers={{
                                        click: () => setSelectedId(entry.id)
                                    }}
                                >
                                    <Popup>
                                        <div className="text-sm">
                                            <div className="font-semibold">
                                                {locationNames[`${entry.lat},${entry.lon}`] || 'Loading...'}
                                            </div>
                                            <div className="text-gray-600">{formatTime(entry.last_seen)}</div>
                                            {entry.server && (
                                                <div className="text-gray-500 text-xs mt-1">{entry.server}</div>
                                            )}
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                    )}
                    </FadeIn>
                </div>

                {/* List */}
                <div className={`${isDesktop ? 'w-1/3 border-l' : 'h-1/2 border-t'} border-gray-700 overflow-y-auto`}>
                    <FadeIn key={loading ? 'loading' : history.length === 0 ? 'empty' : 'list'}>
                    {loading ? (
                        <SkeletonGroup label="Loading location history" className="space-y-1 p-3">
                            <Skeleton className="h-14 w-full" />
                            <Skeleton className="h-14 w-full" />
                            <Skeleton className="h-14 w-full" />
                        </SkeletonGroup>
                    ) : history.length === 0 ? (
                        <EmptyState variant="area">No location history</EmptyState>
                    ) : (() => {
                        // Pre-compute reverse indices for entries with coordinates
                        let coordIndex = validEntries.length;
                        const numbered = history.map((entry) => ({
                            entry,
                            displayNumber: entry.lat !== null && entry.lon !== null ? coordIndex-- : null,
                        }));
                        return (
                            <ListStack
                                items={numbered}
                                getKey={(n) => String(n.entry.id)}
                                on="panel"
                                label="Location history"
                                className="p-2"
                                renderRow={({ entry, displayNumber }) => {
                                    const hasCoords = entry.lat !== null && entry.lon !== null;
                                    const coordKey = hasCoords ? `${entry.lat},${entry.lon}` : null;
                                    const isSelected = selectedId === entry.id;
                                    return (
                                        <ListRow
                                            selected={isSelected}
                                            onClick={hasCoords ? () => setSelectedId(entry.id) : undefined}
                                            actions={hasCoords ? (
                                                <a
                                                    href={`https://www.google.com/maps?q=${entry.lat},${entry.lon}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="rounded-control px-2 py-1 text-xs text-primary hover:text-white"
                                                >
                                                    Map
                                                </a>
                                            ) : undefined}
                                        >
                                            <span className="flex min-w-0 items-start gap-3">
                                                {hasCoords && (
                                                    <span className={`flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs font-bold text-black transition-colors duration-quick ${isSelected ? 'bg-primary' : ''}`} style={isSelected ? undefined : { backgroundColor: CATEGORY.blue.mark.oklch }}>
                                                        {displayNumber}
                                                    </span>
                                                )}
                                                <span className="block min-w-0 flex-1">
                                                    <span className={`block truncate text-sm ${isSelected ? 'text-primary' : 'text-on-surface'}`}>
                                                        {hasCoords
                                                            ? (locationNames[coordKey] || 'Loading...')
                                                            : <span className="text-on-surface-variant italic">No GPS lock</span>
                                                        }
                                                    </span>
                                                    {hasCoords && (
                                                        <span className="block truncate font-mono text-xs text-on-surface-variant">
                                                            {entry.lat}, {entry.lon}
                                                        </span>
                                                    )}
                                                    <span className="mt-1 block text-xs text-on-surface-variant">
                                                        {formatTime(entry.last_seen)}
                                                        {entry.server && (
                                                            <span className="ml-2">• {entry.server}</span>
                                                        )}
                                                    </span>
                                                </span>
                                            </span>
                                        </ListRow>
                                    );
                                }}
                            />
                        );
                    })()}
                    </FadeIn>
                </div>
            </div>
        </Dialog>
    );
}

export default LocationHistoryModal;
